import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import DarkLoadingSpinner from '../components/LoadingSpinner/DarkLoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../layouts/AuthLayout';
import { isKeycloakLoginEnabled } from '../util/constants';
import { ROUTE_PATHS } from './routePaths';

const PublicRoute = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Reactive flag for OIDC callback errors that occur in-place (URL still has ?code=...).
  // Login writes to sessionStorage and dispatches 'oidcCallbackError' so this re-renders
  // without any navigation — avoiding the infinite-loop that navigate() caused.
  const [hasOidcCallbackError, setHasOidcCallbackError] = useState(
    () => Boolean(sessionStorage.getItem('oidcCallbackError'))
  );
  useEffect(() => {
    const handler = () => setHasOidcCallbackError(true);
    window.addEventListener('oidcCallbackError', handler);
    return () => window.removeEventListener('oidcCallbackError', handler);
  }, []);

  const searchParameters = new URLSearchParams(location.search);
  const isLoginPath = location.pathname === ROUTE_PATHS.LOGIN;
  const isOidcCallback =
    isLoginPath &&
    (searchParameters.has('code') || searchParameters.has('session_state'));

  // When Keycloak is enabled, never redirect away from /login so the user
  // always goes through: Login → Keycloak → callback → TERMS_AND_CONDITIONS → Accept → CATEGORY_HOME.
  const useKeycloak = isKeycloakLoginEnabled();
  const stayOnLogin = useKeycloak && isLoginPath;

  if (loading) {
    return <DarkLoadingSpinner />;
  }

  if (user && !stayOnLogin && !isOidcCallback) {
    return <Navigate to="/" replace />;
  }


  if (useKeycloak && isLoginPath) {
    const hasExplicitError =
      searchParameters.has('error') ||
      searchParameters.has('keycloak_error') ||
      hasOidcCallbackError;
    if (hasExplicitError) {
      return (
        <AuthLayout>
          <Outlet />
        </AuthLayout>
      );
    }
    return <Outlet />;
  }

  return (
    <AuthLayout>
      <Outlet />
    </AuthLayout>
  );
};

export default PublicRoute;
